import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button/Button'
import CheckboxField from 'components/Form/CheckboxField/CheckboxField'
import {
  type StreamGoalResponse,
  setStreamGoal,
} from 'services/endpoints/streaming'
import { useUserProfile } from 'store'
import { useStreamingStore } from 'store/streaming'

import LiveModal from '../LiveModal'

import './_tipModal.scss'

interface TipModalProps {
  onClose: () => void
  data: {
    goal: string
    reward: string
    showGoal: boolean
  }
  setTipModalData: (data: {
    goal: string
    reward: string
    showGoal: boolean
  }) => void
}

const TipModal = ({ onClose, data, setTipModalData }: TipModalProps) => {
  const { goal, reward, showGoal } = data
  const [localGoal, setLocalGoal] = useState(goal)
  const [localReward, setLocalReward] = useState(reward)
  const [localShowGoal, setLocalShowGoal] = useState(showGoal)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const streamId = useStreamingStore(state => state.streamId)
  const { t } = useTranslation()

  const currentStreamGoal = useStreamingStore(
    state => state.currentStreamGoal
  )
  const setCurrentStreamGoal = useStreamingStore(
    state => state.setCurrentStreamGoal
  )

  const handleSave = async () => {
    setTipModalData({
      goal: localGoal,
      reward: localReward,
      showGoal: localShowGoal,
    })

    const trimmedGoal = localGoal.trim()
    const amount = trimmedGoal === '' ? null : Number(trimmedGoal)

    const trimmedReward = localReward.trim()
    const rewardToSend =
      localShowGoal && trimmedReward.length > 0 ? trimmedReward : undefined

    try {
      setSaving(true)
      setError(null)

      if (!streamId) {
        throw new Error('No active stream')
      }

      if (amount == null || Number.isNaN(amount) || amount <= 0) {
        setCurrentStreamGoal(null)
        onClose()
        return
      }

      const updated: StreamGoalResponse = await setStreamGoal(
        streamId,
        amount,
        localShowGoal,
        rewardToSend,
        currentStreamGoal?.goal_id ?? null
      )

      setCurrentStreamGoal(updated)
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Failed to save goal')
    } finally {
      setSaving(false)
    }
  }

  return (
    <LiveModal title='Tip Goal' onClose={onClose}>
      <div className='tipmodal'>
        <p className='tipmodal__note'>
          {t('hereYouCanAddTipGoalsForYourStream')}
        </p>

        <div className='tipmodal__input-group goal'>
          <label htmlFor='goal-input'>{t('goal')}</label>
          <div className='input-wrapper'>
            {localGoal && <span>$</span>}
            <input
              style={{ paddingLeft: localGoal ? '1.5rem' : '0' }}
              value={localGoal}
              type='number'
              id='goal-input'
              placeholder={t('noGoal')}
              onChange={e => setLocalGoal(e.target.value)}
              min='0'
              step='0.01'
              inputMode='decimal'
            />
          </div>
        </div>

        <div className='tipmodal__input-group reward'>
          <label htmlFor='reward-input'>{t('reward')}</label>
          <textarea
            value={localReward}
            id='reward-input'
            placeholder={t('rewardOptional')}
            rows={4}
            maxLength={500}
            onChange={e => setLocalReward(e.target.value)}
          />
        </div>

        <div className='char-num'>
          <span>{localReward.length}</span> / 500 {t('characters')}
        </div>

        <div className='tipmodal__consent'>
          <CheckboxField
            id='tipmodalConsent'
            value='show-goal'
            checked={localShowGoal}
            label={t('showTipGoalToVisitors')}
            changeFn={() => setLocalShowGoal(v => !v)}
            customClass='dark-20'
          />
        </div>

        {error && <div className='tipmodal__error'>{error}</div>}

        <div className='tipmodal__buttons'>
          <Button
            text={t('cancel')}
            color='dark-grey'
            font='mont-14-normal'
            type='transparent--borderless'
            width='fit'
            height='3'
            padding='1-15'
            clickFn={onClose}
          />
          <Button
            text={saving ? 'Saving…' : t('save')}
            color='transparent'
            font='mont-14-normal'
            type='transparent--blue'
            width='10'
            height='3'
            clickFn={saving ? undefined : handleSave}
          />
        </div>
      </div>
    </LiveModal>
  )
}

export default TipModal